import { Injectable, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { ObjectUtils, Sorter, Url } from '@ark-libs/ngx-utils';

import { Principal } from '../auth/principal.service';
import { CoreConfigService } from '../config/core-config.service';
import { UserStorageService } from './user-storage.service';
import { UserContextService } from './user-context.service';
import { UaaResourceService } from './uaa-resource.service';
import { UaaOfficeService } from './uaa-office.service';
import { ReadyState, ReadyStateFlag } from './ready-state.model';

@Injectable({ providedIn:'root' })
export class AppSwitcherService {

    private _selector:any;
    private _hideNotPermission:boolean;
    private _showNotPermissionOfficeLevel:string[];

    private userCenterAppLoaded:boolean = false;
    private entryAppLoaded:boolean = false;
    private appletsTreeRootLoaded:boolean = false;
    private bindOfficeAppletsIdLoaded:boolean = false;


    private _userCenterApp:any;
    private entryApp:any;
    private readyState: ReadyStateFlag|string;

    
    private _appletsTree:any;
    private _allApplets:any[] = [];
    private _allAppletSets:any[] = [];
    private _forbidden: boolean = false;
    
    currentAppChange: EventEmitter<any>;
    readyStateChange: EventEmitter<ReadyState> = new EventEmitter();

    constructor(
        private router: Router,
        private principal: Principal,
        private userContext: UserContextService,
        private userStorage: UserStorageService,
		private configService: CoreConfigService,
        private resourceService: UaaResourceService
    ) {
        this._showNotPermissionOfficeLevel = this.configService.appletsShowNotPermissionOfficeLevel;
        this._hideNotPermission = this.configService.appletsHideNotPermission;
        this.currentAppChange = this.userContext.currentAppChange;
        this.principal.authenticationStateChange.subscribe(account=> {
            if(account == null) this.clearData();
            this.initData();
        });
        // this.userContext.currentOfficeChange.subscribe(office=>{
        //     const app = this.userContext.currentApp;
        //     if(office==null&&app!=null&&(app.isUserCenter||app.isEntryApp)) return;
        //     this.initData();
        // });
        this.initData();
    }

    private clearData(){
        this.userCenterAppLoaded = false;
        this.entryAppLoaded = false;
        this.appletsTreeRootLoaded = false;
        this.bindOfficeAppletsIdLoaded = false;
    
        this._userCenterApp = undefined;
        this.entryApp = undefined;
        this.readyState = ReadyStateFlag.LOADING;
        
        this._appletsTree = undefined;
        this._allApplets = [];
        this._allAppletSets = [];
        this._forbidden = false;
    }

    registerSelector(selector:any){
        this._selector = selector;
    }
	get canOpenSelector(){
		return this.configService.canActiveAppletSelector && this._selector && !this.forbidden;
	}
    openSelector(){
        if(this._selector){
            try{this._selector.showModal();}catch{}
        }
    }

    private checkReady(state?: ReadyState){
        if(state==null) state = {state: (this.isReady?ReadyStateFlag.READY:ReadyStateFlag.LOADING)};
        // console.log("AppSwitcherService.checkReady() -> ", this.readyState, state, this.userCenterAppLoaded, this.entryAppLoaded, this.appletsTreeRootLoaded, this.bindOfficeAppletsIdLoaded);

        if(state==ReadyStateFlag.READY){
            this._forbidden = !(this._allApplets.filter(item=>item.hasPermission&&item.visible&&!item.isUserCenter&&!item.isEntryApp).length>0);
        }

        if(this.readyState != state.state){
            this.readyState = state.state;
            this.readyStateChange.emit(state);
        }
    }
    get isReady():boolean{
        return this.userCenterAppLoaded&&this.entryAppLoaded&&this.appletsTreeRootLoaded&&this.bindOfficeAppletsIdLoaded;
    }
    get forbidden(){
        // console.log("AppSwitcherService.forbidden() ->", (!this.isReady?false:this._forbidden), this._allApplets);
        return !this.isReady?false:this._forbidden;
    }
    get appletsTree():any{
        return this._appletsTree;
    }
    get allApplets():any[]{
        return this._allApplets||[];
    }
    get allAppletSets():any[]{
        return this._allAppletSets||[];
    }
    get currentApp(): {id?:number, name?:string, label?:string, fullDisplayName?:string}|any {
    	return this.userContext.currentApp;
    }
    get userCenterApp(){
        return this._userCenterApp;
    }

    findApp(appId){
        if(appId == null) return null;
        const app = this._allApplets.filter(item=>item.id==appId).shift();
        // console.log(appId, this._allApplets, app);
        return app?app:this._allAppletSets.filter(item=>item.id==appId).shift();
    }

    // 切换当前应用使用此方法
    changeCurrentApp(appId:string|number|any):boolean{
        appId = (appId instanceof Object ? appId.id: appId);
        const lastApp = this.currentApp;

        // console.log("changeCurrentApp -->  lastApp=", lastApp, "toAppId=", appId);

        if((lastApp?lastApp.id:null)==appId) return;

        const app = this._allApplets?this._allApplets.filter(item=>item.id==appId||item.name==appId).pop():null;
        this.userStorage.setCurrentApp(ObjectUtils.cloneAndRemoveObjectRef(app, 1));
    }

    // 判断当前用户在当前机构下是否有当前应用的访问权限
    // 没有权限时自动跳转到由routerWhileNotPermission指定的地址，默认跳转到应用首页
    checkCurrentAppPermission(routerWhileNotPermission?:string){
        const app = this.currentApp;
        if(app == null || this.forbidden) return;

        if(routerWhileNotPermission==null) routerWhileNotPermission = "/apps/"+app.id;
        if(this.router.url == routerWhileNotPermission) return;

        const chldren = app.children||[];
        if(this.isAppLock(app)||chldren.length == 0){
            this.router.navigate([routerWhileNotPermission]);
        }

        const currentOffice = this.userContext.currentOffice;
        if(currentOffice!=null){
            const len = app.children.filter(item=>{
                item.hasPermissionOfficeIds!=null&&item.hasPermissionOfficeIds.indexOf(currentOffice.id)==-1
            }).length;
            if(len == 0) this.router.navigate([routerWhileNotPermission]);
        }
    }
    navigateTo(app: any) {
        if(app==null||app.resourceType!="APPLICATION"||this.isAppLock(app)) return;

        const lastApp = this.currentApp;
        if((lastApp?lastApp.id:null)==(app?app.id:null)) return;

        let actionUrl = app.action?Url.relative(app.action):null;
        if(!this.configService.isDevMode&&actionUrl&&!Url.isEqualPath(actionUrl, location.href)){
            // 指定的APP不在当前的应用下，直接地址跳转(开发模式下直接路由跳转)
            let targetUrl = actionUrl.href.split('\?')[0].split("#")[0];
            if(!targetUrl.endsWith('/')) targetUrl += '/';
            location.href = targetUrl + "#/apps/"+app.id;
        }else{
            this.router.navigate(["/apps/"+app.id]);
        }
    }

    navigateToUserCenter(){
        if(this.userCenterApp) this.navigateTo(this.userCenterApp);
    }

    isAppHomeRouterMatch(appOrRouterUrl:string|any, routerUrl:string){
        let appHomeRouter = (appOrRouterUrl instanceof Object ? appOrRouterUrl.appHomeRouter : appOrRouterUrl);
        appHomeRouter = (appHomeRouter||'').split(/\?/)[0];
        routerUrl = (routerUrl||'').split(/\?/)[0];

        if(!appHomeRouter.startsWith('/')) appHomeRouter = "/"+appHomeRouter;
        if(!routerUrl.startsWith('/')) routerUrl = "/"+routerUrl;

        if(!appHomeRouter.endsWith('/')) appHomeRouter += "/";
        if(!routerUrl.endsWith('/')) routerUrl += "/";

        // console.log("isAppHomeRouterMatch() ->", appHomeRouter,routerUrl,appHomeRouter==routerUrl);
        return appHomeRouter==routerUrl;
    }
    isAppHomeRouter404(app:any){
        return app&&(app.appHomeRouter==null||app.isAppHomeRouter404);
    }
    setAppHomeRouter404(app:any){
        if(this.isAppHomeRouter404(app)) return;
        app.isAppHomeRouter404 = true;
        app = this.findApp(app.id);
        if(app) app.isAppHomeRouter404 = true;
    }
    getAppHomeRouter(app:any){
        return app?app.appHomeRouter:null;
    }
    isAppLock(app:any){
        if(app == null||!app.hasPermission) return true;

        const currentOffice = this.userContext.currentOffice;
        if(currentOffice == null) return false;

        const level = currentOffice?currentOffice.officeType:null;
        return app.bindOfficeLevel==level&&(app.hasPermissionOfficeIds==null||app.hasPermissionOfficeIds.indexOf(currentOffice.id)==-1);
    }

    private initData(){
        // console.log("UserContextService.initData() -> ", this.userContext.isAuthenticated, this.userContext.currentUser);
        if(!this.userContext.isAuthenticated) return;
        this.bindOfficeAppletsIdLoaded = false;
        this.loadMyApps();
    }

    private async loadMyApps(){
        
		this.checkReady({state:this.readyState});
        this._hideNotPermission = this.configService.appletsHideNotPermission;

        // 加载个人中心userCenterApp
        await this.tryLoadUserCenterApp();

        // 加载entryApp
        await this.tryLoadEntryApp();

        // 加载appletsApplicationSet
        await this.tryLoadApplicationSet();
        
        // console.log("loadMyApps() ->", this.userCenterAppLoaded, this.entryAppLoaded, this.appletsTreeRootLoaded, this.bindOfficeAppletsIdLoaded);
        if(this.isReady) this.checkReady();
    }


    private async tryLoadUserCenterApp(){
        const userCenterAppId = this.configService.userCenterApplication;
        if (userCenterAppId!=null&&this.userCenterApp===undefined) {
            this.readyState = ReadyStateFlag.LOADING;
            this._userCenterApp = null;
            await this.resourceService.getMyAppTree(userCenterAppId, {officeId:null}).then(data => {
                if(data) data.isUserCenter = true;
                this.parseAppNodes(null, data);
                this._userCenterApp = data;
                this.userCenterAppLoaded = true;
                this.checkReady();
            }).catch(err=>{
                console.log("个人中心加载失败:", err);
                this.checkReady({state:ReadyStateFlag.ERROR, message:"个人中心加载失败"});
            });
        }else{
            this.userCenterAppLoaded = (userCenterAppId==null || this.userCenterApp != null);
        }
    }
    private async tryLoadEntryApp(){
        const entryAppId = this.configService.entryApplication;
        // console.log("entryApplication=", entryAppId);
        if (entryAppId!=null&&this.entryApp===undefined) {
            this.readyState = ReadyStateFlag.LOADING;
            this.entryApp = null;
            await this.resourceService.getMyAppTree(entryAppId, {officeId:null}).then(data => {
                if(data) data.isEntryApp = true;
                this.parseAppNodes(null, data);
                this.entryApp = data;
                this.entryAppLoaded = true;
                this.checkReady();
            }).catch(err=>{
                console.log("入口应用加载失败:", err);
                this.checkReady({state:ReadyStateFlag.ERROR, message:"入口应用加载失败"});
            });
        }else{
            this.entryAppLoaded = (entryAppId == null || this.entryApp != null);
        }
        // console.log("entryApplication=", entryAppId, this.entryApp);
    }
    private async tryLoadApplicationSet(){
        const root = this.configService.appletsApplicationSet;
        const currentOffice = this.userContext.currentOffice;
        if(this._appletsTree===undefined){
            this.readyState = ReadyStateFlag.LOADING;
            this._appletsTree = null;
            await this.resourceService.getMyAppTree(root, {officeId:null,includeAllBindOfficeApp:true}).then(data => {
                if(data == null && this.entryApp){
                    data = { title:'应用', id: this.entryApp.parentId, resourceType:'APPLICATION_SET',  authorities: this.entryApp.authorities , children: [ this.entryApp ] };
                }
                if (data){
                    this.parseAppNodes(null, data);
                    if(data.resourceType=="APPLICATION_SET"&&data.children&&data.children.length==1&&data.children[0].resourceType=="APPLICATION_SET"){
                        data = data.children[0];
                    }
                }

                // if(currentOffice) this.getMyAppIds(root, currentOffice);
                // else this.bindOfficeAppletsIdLoaded = true;

                this._appletsTree = data;

                const checkApp = (theApp)=>{ return theApp !=null && this._allApplets.filter(item=>theApp.id==item.id&&item.hasPermission&&(!this.isAppLock(item))).length > 0; };

                const routerUrl = this.router.url;
                let inRouterAppId = Number.parseInt(routerUrl.split('/')[2]);
                let app = isNaN(inRouterAppId)?null:this.findApp(inRouterAppId);
                if(!checkApp(app)) app = this.currentApp;
                if(!checkApp(app)) app = this.entryApp;
                if(app == null || this.isAppLock(app)){
                    app = this._allApplets.filter(item=>item.hasPermission&&(!this.isAppLock(item))&&item.visible&&!item.isUserCenter&&!item.isEntryApp).shift();
                }
                if(app!=null) this.changeCurrentApp(app);
                // console.log("loadMyApps() -> load applet tree -> ", this._appletsTree, app);
                this.appletsTreeRootLoaded = true;
                this.checkReady();
            }).catch(err=>{
                console.log("应用列表加载失败:", err);
                this.checkReady({state:ReadyStateFlag.ERROR, message:"应用列表加载失败"});
            });
        }else{
            this.appletsTreeRootLoaded = (this._appletsTree != null);
        }

        // 加载相对于当前机构的应用访问权限
        if(this._appletsTree){
            if(currentOffice) await this.getMyAppIds(root, currentOffice);
            else this.bindOfficeAppletsIdLoaded = true;
        }
    }
    private async getMyAppIds(root, currentOffice){
        this.readyState = ReadyStateFlag.LOADING;
        if(!root)root = this.configService.appletsApplicationSet||(this.entryApp?this.entryApp.parentId:null);
        if(!currentOffice) currentOffice = this.userContext.currentOffice;
        await this.resourceService.getMyAppIds(root, {officeId:currentOffice?currentOffice.id:null}).then(data => {
            if(data){ 
                const officeId = currentOffice?currentOffice.id:0;
                data.forEach(appId => {
                    let app = this._allApplets.filter(item=>item.id==appId).pop();
                    if(app==null) return;
                    if(app.hasPermissionOfficeIds==null) app.hasPermissionOfficeIds = [];
                    if(!app.hasPermissionOfficeIds.includes(officeId)) app.hasPermissionOfficeIds.push(officeId)
                });
            }
            // console.log(this._allApplets);
            this.bindOfficeAppletsIdLoaded = true;
            this.checkReady();
        }).catch(err=>{
            console.log("应用权限加载失败:", err);
            this.checkReady({state:ReadyStateFlag.ERROR, message:"应用权限加载失败"});
        });
    }
    private parseAppNodes(parent, item){
        // console.log("UserContextService.loadApplets -> _parseAppNodes()", item);

        item.parent = parent;
        this.setBindOfficeLevel(item);
        item.hasPermission = this.principal.hasAnyAuthority(item.authorities);

        if(item.action!=null){
            item.action = item.action.replace(/\$*\{*localhost\}*|\$*\{host\}/ig, location.hostname);
            item.action = ObjectUtils.resolveParamRef(item.action, item);
        }
        
        if(item.resourceType=="APPLICATION_SET"){
            this._allAppletSets.push(item);
            if(item.children){
                item.children = item.children.sort(Sorter.sortIndexAscend);
                item.children.forEach(child => this.parseAppNodes(item, child));
            }
            item.visible = (item.children&&item.children.filter(child=>child.visible).length>0);
        }else if(item.resourceType=="APPLICATION"){
            this._allApplets.push(item);
            if(item.action){
                if(item.actionType == 'SREF'){
                    item.action = (item.action.startsWith("/")?'':'/')+item.action;
                    item.appHomeRouter = item.action;
                    item.actionType = 'HREF';
                    item.action = "#"+item.action;
                }else{
                    if(item.action.indexOf("#")==-1) item.action = item.action + (item.action.endsWith("/")?"#/":"/#/");
                    else item.appHomeRouter = item.action.substring(item.action.indexOf('#')+1);
                }
            }
        }

        if((!item.hasPermission) && (item.visible) 
            &&(this._hideNotPermission||this._showNotPermissionOfficeLevel.indexOf(item.bindOfficeLevel) == -1)){
            item.visible = false;
        }
        if(item.children){
            item.childAppSets = item.children
                .filter(child=>child.visible&&child.resourceType=='APPLICATION_SET')
                .sort(Sorter.sortIndexAscend);
                item.hasChildAppSets = (item.childAppSets.length > 0);
        }
    }

    private setBindOfficeLevel(uaaResource:any){
        if(!uaaResource) return null;
        if(!uaaResource.bindOfficeLevel && uaaResource.name){

            const levelMap = {
                "platform":null,
                "community":"COMMUNITY",
                "group":"COMMUNITY_GROUP",
                "region":"BRANCH_REGION"
            };

            let guestLevel = uaaResource.name.substring(uaaResource.name.lastIndexOf(".")+1);
            if(levelMap[guestLevel]==null){
                guestLevel = Object.keys(levelMap).filter(item=>uaaResource.name.indexOf('.'+item+'.')!=-1).pop();
            }
            uaaResource.bindOfficeLevel = levelMap[guestLevel]||this.setBindOfficeLevel(uaaResource.parent);
        }
        return uaaResource.bindOfficeLevel;
    }

}